export type LeaveTier = "super_admin" | "admin" | "executive" | "staff";

export function leaveTier(role?: string | null): LeaveTier {
  const n = String(role || "").trim().toLowerCase();
  if (n === "super admin") return "super_admin";
  // Developer / Operations / CRM / Designer / Captain are staff for leave.
  if (n === "admin") return "admin";
  if (n === "executive") return "executive";
  return "staff";
}

function actorLeaveTier(actor: {
  portalRole?: string | null;
  empRole?: string | null;
}): LeaveTier {
  if (actor.portalRole === "Super Admin" || leaveTier(actor.empRole) === "super_admin") {
    return "super_admin";
  }
  return leaveTier(actor.empRole);
}

/** Admin, Super Admin, and Executive manage leave balances for everyone. */
export function canManageLeaveBalances(actor: {
  portalRole?: string | null;
  empRole?: string | null;
}) {
  const who = actorLeaveTier(actor);
  return who === "super_admin" || who === "admin" || who === "executive";
}

/** Super Admin (owner) does not use leave requests for themselves. */
export function canApplyLeaveRequests(actor: {
  portalRole?: string | null;
  empRole?: string | null;
}) {
  return actorLeaveTier(actor) !== "super_admin";
}

/** Admin / Executive / Super Admin can see every employee's leave requests. */
export function canViewTeamLeaveRequests(actor: {
  portalRole?: string | null;
  empRole?: string | null;
}) {
  return canManageLeaveBalances(actor);
}

/**
 * Approve / reject / cancel someone else's leave request.
 * - Super Admin (owner): any other employee
 * - Admin (manager): any other employee (not own)
 * - Executive: anyone except self, Admin, and Super Admin
 * Nobody edits their own request.
 */
export function canReviewLeaveOf(
  actor: { portalRole?: string | null; empRole?: string | null; empId?: number | null },
  target: { role?: string | null; emp_id?: number | null },
) {
  if (target.emp_id != null && actor.empId != null && Number(target.emp_id) === Number(actor.empId)) {
    return false;
  }
  const who = actorLeaveTier(actor);
  const them = leaveTier(target.role);
  if (who === "super_admin") return true;
  if (who === "admin") return true;
  if (who === "executive") return them !== "admin" && them !== "super_admin";
  return false;
}

/** Cancel: staff may cancel their own pending; managers only cancel requests they can review. */
export function canCancelLeaveOf(
  actor: { portalRole?: string | null; empRole?: string | null; empId?: number | null },
  target: { role?: string | null; emp_id?: number | null },
) {
  const own =
    target.emp_id != null && actor.empId != null && Number(target.emp_id) === Number(actor.empId);
  if (own) return actorLeaveTier(actor) === "staff";
  return canReviewLeaveOf(actor, target);
}

/**
 * Who may edit leave balances for a target employee.
 * - Super Admin: anyone (Admin, Executive, staff). Super Admin has no own leave balance.
 * - Admin: anyone else, including Executive — not their own.
 * - Executive: staff / other executives — not own, not Admin, not Super Admin.
 */
export function canAdjustLeaveBalanceOf(
  actor: { portalRole?: string | null; empRole?: string | null; empId?: number | null },
  target?: { role?: string | null; emp_id?: number | null },
) {
  if (!canManageLeaveBalances(actor)) return false;
  const who = actorLeaveTier(actor);
  if (who === "super_admin") return true;
  if (!target) return false;
  const own =
    target.emp_id != null &&
    actor.empId != null &&
    Number(target.emp_id) === Number(actor.empId);
  if (own) return false;
  const them = leaveTier(target.role);
  if (who === "admin") return true;
  if (who === "executive") return them !== "admin" && them !== "super_admin";
  return false;
}
